// Port of webapp/src/properties/* — the inline editors the Card Detail rows
// mount per property type. Upstream has one file per type behind a
// `PropertyType` registry (properties/index.tsx); the clone keeps the switch in
// one place since every editor here is a plain input.

import {ReactElement, RefObject, useEffect, useRef, useState} from 'react'
import classnames from 'classnames'
import {IPropertyTemplate} from '../../types/board'
import {Card} from '../../types/card'
import {IUser} from '../../types/user'
import {Icon, propertyIcons} from '../common/icons'
import {ButtonWithMenu, Menu, MenuItem, MenuSeparator} from '../common/menu'
import {createDatePropertyFromString, encodeDateProperty, optionColorCssVar, parseArrayValue} from '../../state/properties'

type EditorProps = {
    template: IPropertyTemplate
    card: Card
    value: string | string[] | undefined
    usersById: Record<string, IUser>
    readOnly: boolean
    placeholder: string
    onChange: (value: string | string[] | undefined) => void
}

function isoDay(ms: number): string {
    const d = new Date(ms)
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

function fromIsoDay(day: string): number {
    const [y, m, d] = day.split('-').map(Number)
    const date = new Date(y, (m || 1) - 1, d || 1, 12)
    return date.getTime()
}

function OptionChip({option}: {option: {id: string, value: string, color: string}}): ReactElement {
    return (
        <span className='propValue truncate' style={{backgroundColor: optionColorCssVar(option.color)}}>
            {option.value}
        </span>
    )
}

export function PropertyEditor(props: EditorProps): ReactElement {
    const {template, value, usersById, readOnly, placeholder, onChange} = props

    if (readOnly) {
        return <span className='truncate opacity-80'>{String(Array.isArray(value) ? value.join(', ') : value || '')}</span>
    }

    switch (template.type) {
    case 'select':
    case 'multiSelect': {
        const single = template.type === 'select'
        const selected = single ? new Set(value ? [String(value)] : []) : new Set(parseArrayValue(value))
        return (
            <ButtonWithMenu
                className='w-full'
                button={
                    <span className='flex w-full cursor-pointer items-center gap-2'>
                        {selected.size === 0 ? (
                            <span className='flex items-center gap-2 opacity-50'>
                                <Icon name={propertyIcons[template.type] || 'help'} size={14}/>
                                <span className='truncate'>{placeholder}</span>
                            </span>
                        ) : (
                            <span className='flex flex-wrap items-center gap-1'>
                                {[...selected].map((id) => {
                                    const option = template.options.find((o) => o.id === id)
                                    return option ? <OptionChip key={id} option={option}/> : <span key={id} className='propValue truncate bg-main-fg/10'>{id}</span>
                                })}
                            </span>
                        )}
                        <Icon name='chevron-down' size={12} className='ml-auto shrink-0 opacity-60'/>
                    </span>
                }
            >
                <Menu ariaLabel={`选择${template.name}`}>
                    <div className='max-h-64 overflow-auto py-1'>
                        {template.options.length === 0 && <MenuItem disabled={true}>还没有选项</MenuItem>}
                        {template.options.map((option) => (
                            <MenuItem
                                key={option.id}
                                checked={selected.has(option.id)}
                                onClick={() => {
                                    if (single) {
                                        onChange(selected.has(option.id) ? undefined : option.id)
                                    } else {
                                        const next = new Set(selected)
                                        if (next.has(option.id)) {
                                            next.delete(option.id)
                                        } else {
                                            next.add(option.id)
                                        }
                                        onChange(next.size ? [...next] : undefined)
                                    }
                                }}
                            >
                                <span
                                    className='mr-2 inline-block h-3 w-3 shrink-0 rounded-full'
                                    style={{backgroundColor: optionColorCssVar(option.color)}}
                                    aria-hidden={true}
                                />
                                {option.value}
                            </MenuItem>
                        ))}
                    </div>
                    {selected.size > 0 && (
                        <>
                            <MenuSeparator/>
                            <MenuItem icon='close' onClick={() => onChange(undefined)}>清除</MenuItem>
                        </>
                    )}
                </Menu>
            </ButtonWithMenu>
        )
    }

    case 'date': {
        const dp = createDatePropertyFromString(value)
        return (
            <span className='flex items-center gap-2'>
                <input
                    type='date'
                    className='rounded border border-main-fg/20 bg-transparent px-2 py-0.5 text-sm'
                    value={dp.from ? isoDay(dp.from) : ''}
                    onChange={(event) => {
                        const ms = event.target.value ? fromIsoDay(event.target.value) : 0
                        onChange(ms ? encodeDateProperty({from: ms}) : undefined)
                    }}
                />
                {dp.from && (
                    <button type='button' className='IconButton' onClick={() => onChange(undefined)} aria-label='清除日期'>
                        <Icon name='close' size={13}/>
                    </button>
                )}
            </span>
        )
    }

    case 'person': {
        const userId = String(value || '')
        const user = usersById[userId]
        return (
            <ButtonWithMenu
                className='w-full'
                button={
                    <span className='flex w-full cursor-pointer items-center gap-2'>
                        {user ? (
                            <span className='propValue truncate'>
                                <span className='mr-2 flex h-4 w-4 items-center justify-center rounded-full bg-black/10 text-[9px] font-semibold'>
                                    {user.firstname.charAt(0)}
                                </span>
                                {user.username}
                            </span>
                        ) : (
                            <span className='flex items-center gap-2 opacity-50'>
                                <Icon name='user' size={14}/>
                                <span>{placeholder}</span>
                            </span>
                        )}
                        <Icon name='chevron-down' size={12} className='ml-auto opacity-60'/>
                    </span>
                }
            >
                <Menu ariaLabel='选择人员'>
                    <div className='max-h-64 overflow-auto py-1'>
                        {Object.values(usersById).map((candidate) => (
                            <MenuItem
                                key={candidate.id}
                                checked={candidate.id === userId}
                                onClick={() => onChange(candidate.id === userId ? undefined : candidate.id)}
                            >
                                {candidate.username}
                            </MenuItem>
                        ))}
                    </div>
                </Menu>
            </ButtonWithMenu>
        )
    }

    case 'multiPerson': {
        const ids = parseArrayValue(value)
        return (
            <ButtonWithMenu
                className='w-full'
                button={
                    <span className='flex cursor-pointer items-center gap-1'>
                        {ids.length ? ids.map((id) => (
                            <span key={id} className='flex h-5 w-5 items-center justify-center rounded-full bg-black/10 text-[9px] font-semibold' title={usersById[id]?.username}>
                                {(usersById[id]?.firstname || '?').charAt(0)}
                            </span>
                        )) : <span className='opacity-50'>{placeholder}</span>}
                    </span>
                }
            >
                <Menu ariaLabel='选择人员'>
                    <div className='max-h-64 overflow-auto py-1'>
                        {Object.values(usersById).map((candidate) => (
                            <MenuItem
                                key={candidate.id}
                                checked={ids.includes(candidate.id)}
                                onClick={() => {
                                    const next = ids.includes(candidate.id) ? ids.filter((id) => id !== candidate.id) : [...ids, candidate.id]
                                    onChange(next.length ? next : undefined)
                                }}
                            >
                                {candidate.username}
                            </MenuItem>
                        ))}
                    </div>
                </Menu>
            </ButtonWithMenu>
        )
    }

    case 'checkbox': {
        const checked = value === 'true'
        return (
            <label className='flex cursor-pointer items-center gap-2'>
                <input
                    type='checkbox'
                    checked={checked}
                    onChange={(event) => onChange(event.target.checked ? 'true' : undefined)}
                />
                <span className='text-xs opacity-70'>{checked ? '已勾选' : '未勾选'}</span>
            </label>
        )
    }

    case 'number':
        return (
            <input
                type='number'
                className='w-full bg-transparent outline-none tabular-nums'
                placeholder={placeholder}
                value={String(value ?? '')}
                onChange={(event) => onChange(event.target.value === '' ? undefined : event.target.value)}
            />
        )

    case 'createdTime':
    case 'updatedTime':
    case 'createdBy':
    case 'updatedBy':
        return <span className='opacity-70'>{String(Array.isArray(value) ? value.join(', ') : value || '')}</span>

    default:
        return (
            <input
                type={template.type === 'url' ? 'url' : template.type === 'email' ? 'email' : 'text'}
                className='w-full bg-transparent outline-none'
                placeholder={placeholder}
                value={String(value ?? '')}
                onChange={(event) => onChange(event.target.value || undefined)}
            />
        )
    }
}

/** Title row editor: keeps local draft state so IME input isn't clobbered. */
export function InlineTextInput({value, onCommit, placeholder, className, multiline}: {
    value: string
    onCommit: (next: string) => void
    placeholder?: string
    className?: string
    multiline?: boolean
}): ReactElement {
    const [draft, setDraft] = useState(value)
    const ref = useRef<HTMLTextAreaElement | HTMLInputElement>(null)

    useEffect(() => setDraft(value), [value])

    const cls = classnames('w-full bg-transparent outline-none', className)
    if (multiline) {
        return (
            <textarea
                ref={ref as RefObject<HTMLTextAreaElement>}
                className={classnames(cls, 'resize-none')}
                rows={Math.min(10, Math.max(1, draft.split('\n').length))}
                value={draft}
                placeholder={placeholder}
                onChange={(event) => setDraft(event.target.value)}
                onBlur={() => draft !== value && onCommit(draft)}
                onKeyDown={(event) => {
                    if (event.key === 'Escape') {
                        setDraft(value)
                        ref.current?.blur()
                    }
                }}
            />
        )
    }
    return (
        <input
            ref={ref as RefObject<HTMLInputElement>}
            type='text'
            className={cls}
            value={draft}
            placeholder={placeholder}
            onChange={(event) => setDraft(event.target.value)}
            onBlur={() => draft !== value && onCommit(draft)}
            onKeyDown={(event) => {
                if (event.key === 'Enter') {
                    ref.current?.blur()
                } else if (event.key === 'Escape') {
                    setDraft(value)
                    ref.current?.blur()
                }
            }}
        />
    )
}
